import { forwardRef, type KeyboardEvent } from 'react';

import { Search, X } from 'lucide-react';

import { Button } from '@nesso/ui/primitives/button';
import { Input } from '@nesso/ui/primitives/input';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@nesso/ui/primitives/input-group';

type Props = {
    id: string;
    describedBy?: string;
    controls: string;
    activeOptionId?: string;
    value: string;
    onChange: (value: string) => void;
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
    onFocus: () => void;
    onClearSearch: () => void;
    onCloseSearch?: () => void;
    isOpen: boolean;
    isInvalid: boolean;
    placeholder?: string;
    disabled?: boolean;
    variant: 'navigation' | 'inline';
};

export const SearchComboboxInput = forwardRef<HTMLInputElement, Props>(
    (
        {
            id,
            describedBy,
            controls,
            activeOptionId,
            value,
            onChange,
            onKeyDown,
            onFocus,
            onClearSearch,
            onCloseSearch,
            isOpen,
            isInvalid,
            placeholder,
            disabled,
            variant,
        },
        ref,
    ) => {
        if (variant === 'navigation') {
            return (
                <InputGroup className="w-full">
                    <InputGroupAddon>
                        <Search className="size-4" aria-hidden="true" />
                    </InputGroupAddon>
                    <InputGroupInput
                        ref={ref}
                        id={id}
                        type="text"
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        onFocus={onFocus}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        placeholder={placeholder}
                        disabled={disabled}
                        aria-autocomplete="list"
                        aria-activedescendant={activeOptionId}
                        aria-controls={isOpen ? controls : undefined}
                        aria-describedby={describedBy}
                        aria-expanded={isOpen}
                        aria-invalid={isInvalid}
                        role="combobox"
                    />
                    {(onCloseSearch || value.length > 0) && (
                        <InputGroupAddon align="inline-end">
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon-sm"
                                aria-label={value.length > 0 ? 'Clear search' : 'Close search'}
                                disabled={disabled}
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (value.length > 0) {
                                        onClearSearch();
                                        return;
                                    }
                                    onCloseSearch?.();
                                }}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <X className="size-4" aria-hidden="true" />
                            </Button>
                        </InputGroupAddon>
                    )}
                </InputGroup>
            );
        }

        return (
            <div className="relative">
                <Input
                    ref={ref}
                    id={id}
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    onFocus={onFocus}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full h-9 pl-3 pr-10 z-10"
                    placeholder={placeholder}
                    disabled={disabled}
                    aria-autocomplete="list"
                    aria-controls={isOpen ? controls : undefined}
                    aria-describedby={describedBy}
                    aria-expanded={isOpen}
                    aria-invalid={isInvalid}
                    role="combobox"
                />
                {value.length > 0 && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label="Clear search"
                        disabled={disabled}
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClearSearch();
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                        <X className="size-4" aria-hidden="true" />
                    </Button>
                )}
            </div>
        );
    },
);

SearchComboboxInput.displayName = 'SearchComboboxInput';
